import { AnimatePresence, motion } from "framer-motion";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";
import { Textarea } from "src/sudokit/components/Textarea";
import { cn } from "src/lib/cn";

const FADE_FAST = { duration: 0.15 };
const CHECK_POP = { type: "spring" as const, stiffness: 500, damping: 25 };

export type AgenticChatFeedbackPopoverProps = {
  liked: -1 | 1;
  feedbackText: string;
  onFeedbackTextChange: (value: string) => void;
  feedbackSubmitting: boolean;
  feedbackSubmitted: boolean;
  onSubmit: () => void;
  onSkip: () => void;
  onTryAgain?: () => void;
  onSwitchToChatMode?: () => void;
  onStartFreshChat?: () => void;
  showRepeatedDislikeRecovery?: boolean;
  showSwitchToChatMode?: boolean;
  tryAgainDisabled?: boolean;
};

export function AgenticChatFeedbackPopover({
  liked,
  feedbackText,
  onFeedbackTextChange,
  feedbackSubmitting,
  feedbackSubmitted,
  onSubmit,
  onSkip,
  onTryAgain,
  onSwitchToChatMode,
  onStartFreshChat,
  showRepeatedDislikeRecovery = false,
  showSwitchToChatMode = false,
  tryAgainDisabled = false,
}: AgenticChatFeedbackPopoverProps) {
  const isDislike = liked === -1;

  return (
    <div className="relative">
      <div
        className={cn(
          "flex flex-col gap-3 p-4 transition-opacity duration-150",
          feedbackSubmitted && "opacity-0 pointer-events-none",
        )}
      >
        <div className="flex flex-col gap-1">
          <Text className="text-base font-semibold">
            {liked === 1 ? "What did you like?" : "What could be better?"}
          </Text>
          {isDislike ? (
            <Text className="text-sm text-app-grey-1">
              Tell us what missed the mark about this response. Optional — we read every report.
            </Text>
          ) : null}
        </div>

        {isDislike && showRepeatedDislikeRecovery ? (
          <div className="rounded-[10px] border border-app-grey-3 bg-app-grey-4/40 px-3 py-2 flex flex-col gap-2">
            <Text className="text-sm text-app-grey-0">
              Looks like this chat is going sideways. You can try again, switch modes, or start fresh.
            </Text>
            <div className="flex flex-wrap gap-2">
              {showSwitchToChatMode && onSwitchToChatMode ? (
                <SudoButton
                  variant="secondary"
                  size="small"
                  onClick={onSwitchToChatMode}
                  disabled={feedbackSubmitting}
                >
                  Switch to Chat mode
                </SudoButton>
              ) : null}
              {onStartFreshChat ? (
                <SudoButton
                  variant="tertiaryGrey"
                  size="small"
                  onClick={onStartFreshChat}
                  disabled={feedbackSubmitting}
                >
                  Start a new chat
                </SudoButton>
              ) : null}
            </div>
          </div>
        ) : null}

        <Textarea
          value={feedbackText}
          onChange={(event: { target: { value: string } }) => onFeedbackTextChange(event.target.value)}
          onKeyDown={(event: { key: string; metaKey?: boolean; ctrlKey?: boolean; preventDefault: () => void }) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              onSubmit();
            }
          }}
          placeholder={isDislike ? "What went wrong with this response?" : "Optional feedback..."}
          mode="default"
          className="!rounded-[10px]"
          minRows={3}
          short
          autoFocus
        />

        <div className="flex items-center justify-end gap-2">
          <SudoButton
            variant="tertiaryGrey"
            size="small"
            onClick={onSkip}
            disabled={feedbackSubmitting}
          >
            Skip
          </SudoButton>
          {isDislike && onTryAgain ? (
            <SudoButton
              variant="secondary"
              size="small"
              onClick={onTryAgain}
              disabled={tryAgainDisabled || feedbackSubmitting}
            >
              Try again
            </SudoButton>
          ) : null}
          <SudoButton
            variant="primary"
            size="small"
            state={feedbackSubmitting ? "loading" : "default"}
            onClick={onSubmit}
            rightIcon="/assets/send.svg"
          >
            Submit
          </SudoButton>
        </div>
      </div>

      <AnimatePresence>
        {feedbackSubmitted ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={FADE_FAST}
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-app-menu-color"
          >
            <motion.div
              initial={{ scale: 0.3, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ scale: CHECK_POP, opacity: { duration: 0.1 } }}
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                <circle cx="16" cy="16" r="16" className="fill-app-primary-cta-high-contrast" />
                <motion.path
                  d="M9.5 16.5L14 21L22.5 11"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
                />
              </svg>
            </motion.div>
            <Text className="text-sm text-app-grey-0 text-center px-4">
              Thanks — we use this to improve Sudowrite.
            </Text>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
